import React, { useMemo } from 'react';
import { Alert, Stack, InputAdornment, IconButton } from '@mui/material';

import {
  Button,
  ButtonItem as ButtonGroupItem,
  Typography,
  ClearIcon,
} from '@occc/ui-design-system';
import { TextField, ToggleButtonGroupField, Select } from '@occc-app/design-system';
import { Adjust_Points_Mathematical_Function } from '@occc/graphql';
import { parseNumber } from '@occc-app/lib/finalFormUtils';
import { useChainConfiguration } from '@occc-app/Chain/hooks';

import { UpdatePointsModalFormProps } from './types';

const useButtonItems = (): ButtonGroupItem[] => {
  return useMemo(
    () => [
      {
        title: 'Add',
        value: Adjust_Points_Mathematical_Function.IncreasePointOperation,
      },
      {
        title: 'Deduct',
        value: Adjust_Points_Mathematical_Function.DecreasePointOperation,
      },
    ],
    [],
  );
};

const UpdatePointsModalForm = ({
  onHide,
  handleSubmit,
  submitting,
  pristine,
  invalid,
  customerUuid,
  isAdjustmentReasonSelected,
  onAdjustmentReasonClearClick,
}: UpdatePointsModalFormProps) => {
  const { allowedPointAdjustmentReasons } = useChainConfiguration();
  const disableSubmit = pristine || submitting || invalid || !customerUuid;
  const buttons = useButtonItems();

  const adjustmentReasons = useMemo(
    () =>
      allowedPointAdjustmentReasons.map((reason) => ({
        label: reason.label,
        value: reason.id,
      })),
    [allowedPointAdjustmentReasons],
  );

  return (
    <Stack spacing={2} component='form' onSubmit={handleSubmit} pt={2}>
      <Stack direction='row' spacing={2}>
        <ToggleButtonGroupField name='mathematicalFunction' buttons={buttons} />
        <TextField type='number' name='points' label='Points' fieldProps={{ parse: parseNumber }} />
      </Stack>
      {allowedPointAdjustmentReasons.length > 0 && (
        <Select
          defaultValue=''
          name='adjustmentReasonId'
          label='Adjustment Reason'
          data={adjustmentReasons}
          endAdornment={
            isAdjustmentReasonSelected && (
              <InputAdornment sx={{ mr: 1 }} position='end'>
                <IconButton onClick={onAdjustmentReasonClearClick}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }
        />
      )}
      <TextField name='notes' label='Notes' multiline={true} rows={4} />
      {!customerUuid && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          <Typography>
            This member does not have a customer ID. Please contact your TPM to help resolve this
            issue.
          </Typography>
        </Alert>
      )}
      <Stack direction='row' spacing={2} justifyContent='flex-end'>
        <Button variant='outlined' onClick={onHide}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' type='submit' disabled={disableSubmit}>
          Submit
        </Button>
      </Stack>
    </Stack>
  );
};

export default UpdatePointsModalForm;
